<template>
  <Modal @close="emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <p class="text-gray-900">
            Statuses to include in the summary
          </p>

          <div class="flex flex-wrap gap-3">
            <button
              type="button"
              class="btn btn--secondary-blue"
              @click="selectAll"
            >
              {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.selectAll') }}
            </button>

            <button
              type="button"
              class="btn btn--secondary-blue"
              @click="clearAll"
            >
              {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.clearAll') }}
            </button>
          </div>
        </div>

        <fieldset class="grid grid-cols-1 gap-2 border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
          <legend class="sr-only">
            {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.statusLegend') }}
          </legend>

          <label
            v-for="opt in statusOptions"
            :key="opt.value"
            class="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
          >
            <input
              v-model="statusForm.form.statuses"
              type="checkbox"
              name="variation-status"
              class="form-checkbox"
              :value="opt.value"
            >

            <span class="text-sm text-gray-800">{{ opt.label }}</span>
          </label>
        </fieldset>

        <p
          v-if="statusForm.form.statuses.length === 0"
          class="text-sm text-red-600"
          role="alert"
        >
          {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.needsOneStatus') }}
        </p>

        <div>
          <p class="text-gray-900 mb-2">
            {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.sortBy') }}
          </p>

          <fieldset class="flex flex-wrap gap-4">
            <legend class="sr-only">
              {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.sortLegend') }}
            </legend>

            <label
              v-for="opt in sortOptions"
              :key="opt.value"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                v-model="statusForm.form.sort"
                type="radio"
                name="variation-summary-sort"
                class="form-radio"
                :value="opt.value"
              >

              <span class="text-sm text-gray-800">{{ opt.label }}</span>
            </label>
          </fieldset>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-between space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          :disabled="summaryReportAction.is(States.LOADING)"
          @click="emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="summaryReportAction.is(States.LOADING) || statusForm.form.statuses.length === 0"
          @click="submit"
        >
          {{ $t('HeadContract.VariationsSchedule.SummaryReportModal.submit') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { VariationOrderStatus, VariationOrderStatusTitles } from '@/models/VariationOrder';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, onMounted, watch } from 'vue';

const props = defineProps<{
  projectId: Id;
}>();

const emit = defineEmits<{
  close: [];
}>();

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.models.find(props.projectId);
});

const statusForm = useFormData({
  statuses: [],
  sort: 'code',
});

const sortOptions = [
  { value: 'code', label: t('HeadContract.VariationsSchedule.SummaryReportModal.sortByCode') },
  { value: 'status', label: t('HeadContract.VariationsSchedule.SummaryReportModal.sortByStatus') },
];

const allStatusValues = computed(() => {
  const values = Object.values(VariationOrderStatus);
  let filteredValues = [];

  if(project.value && project.value.isContractor) {
    filteredValues = values.filter((value) => {
      return value !== VariationOrderStatus.declined;
    });
  } else {
    filteredValues = values.filter((value) => {
      return value !== VariationOrderStatus.submitted &&
        value !== VariationOrderStatus.declined;
    });
  }

  return filteredValues;
});

onMounted(() => {
  statusForm.form.statuses = allStatusValues.value;
});

const statusOptions = computed(() => {
  const options = Object.keys(VariationOrderStatus) as (keyof typeof VariationOrderStatus)[];
  let filteredOptions = [];

  if(project.value && project.value.isContractor) {
    filteredOptions = options;
  } else {
    filteredOptions = options.filter((value) => {
      return value !== 'submitted';
    });
  }

  return filteredOptions.map((key) => ({
    value: VariationOrderStatus[key],
    label: VariationOrderStatusTitles[key],
  }));
});

const variationOrdersStore = useVariationOrdersStore();
const summaryReportAction = useStoreApiAction(variationOrdersStore.requestVariationsSummaryReport);

const selectAll = () => {
  statusForm.form.statuses = allStatusValues.value;
};

const clearAll = () => {
  statusForm.form.statuses = [];
};

const submit = () => {
  if(statusForm.form.statuses.length === 0) {
    return;
  }

  summaryReportAction.request(props.projectId, statusForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      title: t('HeadContract.VariationsSchedule.SummaryReportModal.successTitle'),
      text: t('HeadContract.VariationsSchedule.SummaryReportModal.successText'),
    }).then(() => {
      emit('close');
    });
  }).catch((error) => {
    console.log(error);

    if(error?.status === 429) {
      Swal.fire({
        icon: 'error',
        title: t('HeadContract.VariationsSchedule.SummaryReportModal.errorTitle'),
        text: error.data.message,
      });
    }
  });
};
</script>
